import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Swal from 'sweetalert2';
import { QRCodeSVG } from 'qrcode.react';
import { db } from '../../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import MarketingKit from './MarketingKit';
import Payouts from './Payouts';

const FranchiseDashboard = () => {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const tabFromUrl = queryParams.get('tab') || 'overview';
    
    const franchiseName = user?.franchiseData?.franchiseName || 'Your Franchise';
    const referralCode = user?.franchiseData?.referralCode || 'N/A';
    
    const [activities, setActivities] = useState([]);
    const [totalReferrals, setTotalReferrals] = useState(0);
    const [withdrawnAmount, setWithdrawnAmount] = useState(0);
    const [savedThemeName, setSavedThemeName] = useState('Dark Charcoal');
    const [customColor1, setCustomColor1] = useState('#1e3a8a');
    const [customColor2, setCustomColor2] = useState('#0f172a');
    const [customAccent, setCustomAccent] = useState('#38bdf8');
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const itemsPerPage = 10;

    // Update URL when tab changes
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        navigate(`/franchise/dashboard?tab=${tab}`, { replace: true });
    };

    useEffect(() => {
        if (tabFromUrl && ['overview', 'marketing', 'payouts'].includes(tabFromUrl)) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (user?.id || user?.uid) {
                try {
                    const userId = user.id || user.uid;
                    const adminRef = doc(db, 'franchiseAdmins', userId);
                    const adminSnap = await getDoc(adminRef);
                    if (adminSnap.exists()) {
                        const data = adminSnap.data();
                        setTotalReferrals(data.totalReferrals || 0);
                        setActivities(data.recentActivity || []);
                        setWithdrawnAmount(data.withdrawnAmount || 0);
                        setSavedThemeName(data.posterTheme || 'Dark Charcoal');
                        if (data.customColor1) setCustomColor1(data.customColor1);
                        if (data.customColor2) setCustomColor2(data.customColor2);
                        if (data.customAccent) setCustomAccent(data.customAccent);
                    }
                } catch (error) {
                    console.error("Error fetching franchise dashboard data:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchDashboardData();
    }, [user]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentActivities = activities.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(activities.length / itemsPerPage);

    const copyReferralCode = () => {
        if (referralCode !== 'N/A') {
            navigator.clipboard.writeText(referralCode);
            Swal.fire({
                title: 'Copied!',
                text: 'Referral code copied to clipboard.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        }
    };

    const referralLink = `${window.location.origin}/begin-with-clarity?ref=${referralCode}`;
    const copyReferralLink = () => {
        if (referralCode !== 'N/A') {
            navigator.clipboard.writeText(referralLink);
            Swal.fire({
                title: 'Copied!',
                text: 'Referral link copied to clipboard.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        }
    };

    // Calculate revenue (₹500 per completed referral, just as an example metric)
    const completedReferrals = activities.filter(a => a.status === 'Completed').length;
    const totalRevenue = completedReferrals * 500;
    
    // Target calculation
    const targetReferrals = 50;
    const progressPercent = Math.min((completedReferrals / targetReferrals) * 100, 100);

    return (
        <div className="admin-content-v2">
            <div className="admin-header-v2" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h1 className="admin-title-v2">Welcome, {user?.name || 'Franchise Partner'}</h1>
                        <p className="admin-subtitle-v2">Here's an overview of {franchiseName}</p>
                    </div>
                    <div style={{ background: 'white', padding: '1rem 1.5rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your Referral Code</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1a7a5e', letterSpacing: '1px' }}>{referralCode}</div>
                        </div>
                        <button 
                            onClick={copyReferralCode}
                            style={{ background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Copy Code"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                        </button>
                    </div>
                </div>
            </div>

            {activeTab === 'overview' && (
                <>
                    <div className="stats-grid-v2">
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Total Referrals</span>
                    <span className="stat-value-v2">{isLoading ? '...' : totalReferrals}</span>
                    <div className="stat-trend-v2 positive">
                        <span>New</span>
                        <span className="stat-sub-v2">this month</span>
                    </div>
                </div>

                <div className="stat-card-v2">
                    <span className="stat-label-v2">Franchise Revenue</span>
                    <span className="stat-value-v2">₹{totalRevenue.toLocaleString('en-IN')}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Active</span>
                        <span className="stat-sub-v2">earnings</span>
                    </div>
                </div>
                
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Diagnosis Conducted</span>
                    <span className="stat-value-v2">{completedReferrals}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Total</span>
                        <span className="stat-sub-v2">sessions completed</span>
                    </div>
                </div>
            </div>

            {/* Target Progress Bar */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>Next Bonus Target: {targetReferrals} Referrals</span>
                    <span style={{ fontWeight: 700, color: '#1a7a5e' }}>{completedReferrals} / {targetReferrals}</span>
                </div>
                <div style={{ width: '100%', background: '#e2e8f0', borderRadius: '8px', height: '10px', overflow: 'hidden' }}>
                    <div style={{ width: `${progressPercent}%`, background: 'linear-gradient(90deg, #34d399 0%, #10b981 100%)', height: '100%', borderRadius: '8px', transition: 'width 0.5s ease' }}></div>
                </div>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                    {targetReferrals - completedReferrals > 0 
                        ? `Just ${targetReferrals - completedReferrals} more to unlock your ₹5,000 Bonus!` 
                        : 'Target Reached! Bonus Unlocked!'}
                </p>
            </div>
            
            <div className="table-card-v2" style={{ marginTop: '2rem' }}>
                <div className="table-card-header-v2" style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
                    <h3 style={{ margin: 0, color: '#1e293b' }}>Recent Activity</h3>
                </div>
                <div className="table-responsive">
                    <table className="v2-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Type</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Name</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Date</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Health Score</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan="5" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                                        Loading activity...
                                    </td>
                                </tr>
                            ) : activities.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                                        No recent activity to show yet. Share your franchise link to get started!
                                    </td>
                                </tr>
                            ) : (
                                currentActivities.map((activity, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '1rem 1.5rem', color: '#1e293b', fontWeight: 500 }}>{activity.type}</td>
                                        <td style={{ padding: '1rem 1.5rem', color: '#475569' }}>{activity.name}</td>
                                        <td style={{ padding: '1rem 1.5rem', color: '#475569' }}>{activity.date}</td>
                                        <td style={{ padding: '1rem 1.5rem', color: '#475569' }}>{activity.healthScore || 'N/A'}</td>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <span style={{ 
                                                padding: '4px 8px', 
                                                borderRadius: '4px', 
                                                fontSize: '0.75rem', 
                                                fontWeight: 600,
                                                background: activity.status === 'Completed' ? '#dcfce7' : '#fef9c3',
                                                color: activity.status === 'Completed' ? '#166534' : '#854d0e'
                                            }}>
                                                {activity.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="pagination-v2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <button 
                            disabled={currentPage === 1} 
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            style={{ background: 'white', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '6px', color: '#475569', fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                        >
                            Previous
                        </button>
                        <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>Page {currentPage} of {totalPages}</span>
                        <button 
                            disabled={currentPage === totalPages} 
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            style={{ background: 'white', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '6px', color: '#475569', fontWeight: 600, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
            </>
            )}

            {activeTab === 'marketing' && (
                <MarketingKit 
                    userId={user?.id || user?.uid}
                    franchiseName={franchiseName} 
                    referralCode={referralCode} 
                    referralLink={referralLink} 
                    savedThemeName={savedThemeName}
                    dbCustomColor1={customColor1}
                    dbCustomColor2={customColor2}
                    dbCustomAccent={customAccent}
                    userEmail={user?.email || ''}
                    userPhone={user?.franchiseData?.mobile || user?.mobile || user?.phone || ''}
                />
            )}

            {activeTab === 'payouts' && (
                <Payouts 
                    userId={user?.id || user?.uid} 
                    totalRevenue={totalRevenue} 
                    withdrawnAmount={withdrawnAmount}
                    activities={activities} 
                />
            )}
        </div>
    );
};

export default FranchiseDashboard;
